/* ==========================================================================
   Suit Heritage — Stiletto-native sizing
   - Length is chosen with a segmented control (Short / Regular / Long / Custom)
   - Numbers that are not offered in that length render struck through
   - No network requests: every availability check is local, so it is instant
   ========================================================================== */

(() => {
  'use strict';

  if (!window.customElements || customElements.get('sh-size')) return;

  const LENGTHS = [
    { code: 'S', label: 'Short' },
    { code: 'R', label: 'Regular' },
    { code: 'L', label: 'Long' },
    /* Not a real length. Picking it sets the size to the custom (made to
       measure) value, which carries no number and no length code. */
    { code: 'C', label: 'Custom', isCustom: true }
  ];

  const OWNED_PROPS = [
    'Jacket Size',
    'Pant Size',
    'Jacket Fit',
    'Pant Fit',
    'Sizing Method'
  ];

  /* State survives a theme section re-render (variant change repaints the DOM). */
  const MEMORY = new Map();

  const toList = (value) =>
    String(value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

  const norm = (value) => String(value == null ? '' : value).trim().toLowerCase();

  const numeric = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const svg = (path) =>
    `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${path}"/></svg>`;

  const isRealCartForm = (form) =>
    Boolean(form) &&
    !/installment/i.test(form.id || '') &&
    Boolean(form.querySelector('[name="add"], button[type="submit"]'));

  /*
    One capture listener on the document instead of one per form. Themes swap
    the product form out on every variant change, and a sticky add-to-cart bar
    submits a second form entirely — both are covered from here.
  */
  let guardInstalled = false;

  function installGuard() {
    if (guardInstalled) return;
    guardInstalled = true;

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!form?.matches?.('form[action*="/cart/add"]')) return;
        if (!isRealCartForm(form)) return;

        const scope = form.closest(
          '.product[data-section-type="product"], .quick-product, [data-shz-scope]'
        );

        const panel =
          (scope && scope.querySelector('sh-size.shz--product')) ||
          form.shzOwner ||
          document.querySelector('sh-size.shz--product');

        if (!panel?.booted) return;

        if (panel.requireSize && !panel.complete()) {
          event.preventDefault();
          event.stopImmediatePropagation();

          const missing =
            panel.garments.find((garment) => !panel.formatted(garment)) ||
            panel.garments[0];

          panel.showNote(
            panel.copy.requiredMessage || 'Choose a size before adding to cart.'
          );
          panel.open(missing);
          return;
        }

        if (panel.complete() && panel.variants.length && !panel.resolveVariant()) {
          event.preventDefault();
          event.stopImmediatePropagation();

          const hasCustom = panel.garments.some((garment) =>
            panel.isCustom(panel.state[garment].n)
          );
          panel.showNote('That size combination is not available. Choose another size.');
          panel.open(panel.garments[0]);
          return;
        }

        /* Write into whichever form is actually submitting. */
        panel.writeProperties(form);
      },
      true
    );
  }

  class SHSize extends HTMLElement {
    connectedCallback() {
      if (this.booted) return;
      this.booted = true;

      this.config();
      this.cacheNodes();
      this.restore();
      this.bind();
      this.paintTriggers();
      this.guardForm();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.booted = false;
      document.body.classList.remove('shz-lock');
    }

    /* ------------------------------------------------------------- config */

    config() {
      const data = this.dataset;

      this.panelType = data.panelType || 'suit';
      this.sizingProfile = data.sizingProfile === 'women' ? 'women' : 'men';
      this.lengthLabels = this.sizingProfile === 'women'
        ? { S: 'Petite', R: 'Classic', L: 'Tall', C: 'Custom' }
        : { S: 'Short', R: 'Regular', L: 'Long', C: 'Custom' };
      this.requireSize = data.requireSize !== 'false';
      this.garments =
        this.panelType === 'jacket'
          ? ['jacket']
          : this.panelType === 'pants'
            ? ['pants']
            : ['jacket', 'pants'];

      this.copy = this.readJSON('[data-shz-copy]', {});
      this.product = this.readJSON('[data-shz-product]', { id: 0, options: [] });
      this.variants = this.readJSON('[data-shz-variants]', []);
      this.indexVariants();
      this.normaliseOptions();

      this.optionNames = {
        jacket: data.jacketOption || 'Jacket Size',
        pants: data.pantOption || 'Pant Size',
        jacketFit: data.jacketFitOption || 'Jacket Fit',
        pantsFit: data.pantFitOption || 'Pant Fit'
      };

      this.help = {
        S: data.helpS || '',
        R: data.helpR || '',
        L: data.helpL || ''
      };

      this.presets = {
        jacket: {
          S: new Set(toList(data.jacketS || '34,36,38,40,42,44,46,48,50,52,54,56,58,60,62,64')),
          R: new Set(toList(data.jacketR || '34,36,38,40,42,44,46,48,50,52,54,56,58,60,62,64')),
          L: new Set(toList(data.jacketL || '38,40,42,44,46,48,50,52,54,56,58,60'))
        },
        pants: {
          S: new Set(toList(data.pantsS || '28,30,32,34,36,38,40,42,44,46,48,50,52,54,56,58')),
          R: new Set(toList(data.pantsR || '28,30,32,34,36,38,40,42,44,46,48,50,52,54,56,58')),
          L: new Set(toList(data.pantsL || '32,34,36,38,40,42,44,46,48,50,52,54'))
        }
      };

      /*
        A "Custom" size is length-less: it is one option value, not a number
        crossed with Short/Regular/Long. Everything below treats it specially so
        it never renders as "CustomR".
      */
      this.customValue = (this.dataset.shzCustomValue || 'Custom').trim();
      this.customKey = this.customValue.toLowerCase();
      this.sizeGuideEnabled = this.dataset.shzSizeGuideEnabled === 'true';
      this.sizeGuideUrl = this.dataset.shzSizeGuideUrl || '/pages/size-guide';
      this.sizeGuideLabel = this.dataset.shzSizeGuideLabel || 'View Size Charts';
      this.sizeGuideSource = null;
      this.sizeGuidePromise = null;
      this.sizeGuideRenderToken = 0;
      this.sizeGuideGarment = '';


      this.numbers = {
        jacket: this.deriveNumbers('jacket'),
        pants: this.deriveNumbers('pants')
      };

      this.state = {
        jacket: { n: '', l: '', fit: '' },
        pants: { n: '', l: '', fit: '' }
      };

      this.draft = { n: '', l: '' };
      this.view = this.garments[0];
      this.grids = {};

      this.memoryKey = `${this.product.id || 'p'}:${this.panelType}`;
      this.productRoot = this.closest(
        '.product[data-section-type="product"], .quick-product, [data-shz-scope]'
      );
      this.formEl = this.findForm();

      if (this.productRoot && this.dataset.hideDynamic === 'true') {
        this.productRoot.classList.add('shz-hide-dynamic');
      }
    }

    /*
      One pass over the variant list gives us "is this option value in stock",
      which is everything the matrix needs. No per-click lookups, no fetches.
    */
    indexVariants() {
      this.stock = new Map();

      this.variants.forEach((variant) => {
        (variant.o || []).forEach((value, slot) => {
          const key = `${slot + 1}|${norm(value)}`;
          if (!this.stock.get(key)) {
            this.stock.set(key, variant.a !== false);
          }
        });
      });
    }

    /* Shopify returns option values as plain strings on some themes and as
       objects on others. Flatten both into { name, available, selected }. */
    normaliseOptions() {
      this.product.options = (this.product.options || []).map((option, index) => {
        const position = Number(option.position) || index + 1;
        const selected = option.selected ?? option.selected_value ?? '';

        const values = (option.values || []).map((value) => {
          const name = typeof value === 'string' ? value : value?.name ?? '';
          let available = true;

          if (this.variants.length) {
            available = this.stock.get(`${position}|${norm(name)}`) === true;
          } else if (typeof value === 'object' && value?.available != null) {
            available = value.available !== false;
          }

          return { name, available, selected: norm(name) === norm(selected) };
        });

        return { name: option.name, position, selected, values };
      });
    }

    cacheNodes() {
      this.dialog = this.querySelector('[data-shz-dialog]');
      this.titleEl = this.querySelector('[data-shz-title]');
      this.segRow = this.querySelector('[data-shz-lengths]');
      this.gridTitle = this.querySelector('[data-shz-grid-title]');
      this.gridField = this.querySelector('[data-shz-size-field]');
      this.gridWrap = this.querySelector('[data-shz-grid]');
      this.msg = this.querySelector('[data-shz-msg]');
      this.note = this.querySelector('[data-shz-note]');
      this.confirmBtn = this.querySelector('[data-shz-confirm]');
      this.confirmLabel = this.confirmBtn?.textContent.trim() || 'Select this size';
      this.fitWrap = this.querySelector('[data-shz-fit]');
      this.fitRow = this.querySelector('[data-shz-fit-options]');
      this.fitLabel = this.querySelector('[data-shz-fit-label]');
      this.chartToggle = this.querySelector('[data-shz-chart-toggle]');
      this.chartPanel = this.querySelector('[data-shz-chart-panel]');
      this.chartTitle = this.querySelector('[data-shz-chart-title]');
      this.chartContent = this.querySelector('[data-shz-chart-content]');
      this.chartHide = this.querySelector('[data-shz-chart-hide]');
    }

    bind() {
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.querySelectorAll('[data-shz-open]').forEach((button) => {
        button.addEventListener(
          'click',
          () => this.open(button.dataset.shzOpen),
          { signal }
        );
      });

      this.querySelectorAll('[data-shz-close]').forEach((button) => {
        button.addEventListener('click', () => this.close(), { signal });
      });

      this.confirmBtn?.addEventListener('click', () => this.commit(), { signal });
      this.chartToggle?.addEventListener('click', () => this.toggleSizeGuide(), { signal });
      this.chartHide?.addEventListener('click', () => this.hideSizeGuide(), { signal });


      /* Click on the backdrop (the dialog element itself) closes the panel. */
      this.dialog?.addEventListener(
        'click',
        (event) => {
          if (event.target === this.dialog) this.close();
        },
        { signal }
      );

      this.dialog?.addEventListener(
        'cancel',
        (event) => {
          event.preventDefault();
          this.close();
        },
        { signal }
      );
    }

    /* -------------------------------------------------------- size tables */

    deriveNumbers(garment) {
      const found = new Set();

      LENGTHS.forEach((length) => {
        if (length.isCustom) return;
        this.presets[garment][length.code].forEach((number) => found.add(number));
      });

      if (!found.size) {
        const option = this.optionFor(garment);
        (option?.values || []).forEach((value) => {
          const match = String(value.name)
            .trim()
            .toUpperCase()
            .match(/^(\d{1,3})\s*(S|R|L)?$/);
          if (match) found.add(match[1]);
        });
      }

      return [...found]
        .filter((number) => !this.isCustom(number))
        .sort((a, b) => numeric(a) - numeric(b));
    }

    hasPreset(garment) {
      return LENGTHS.some(
        (length) => !length.isCustom && this.presets[garment][length.code].size > 0
      );
    }

    /*
      A number is offered when the preset list allows it and, if the store also
      sells it as a real Shopify option value, that value is in stock.
    */
    isCustom(number) {
      return String(number || '').trim().toLowerCase() === this.customKey;
    }

    valueForState(garment, state) {
      const option = this.optionFor(garment);
      if (!option || !state?.n) return null;

      return this.isCustom(state.n)
        ? this.optionValue(option, this.customValue)
        : this.optionValue(option, `${state.n}${state.l}`) ||
            this.optionValue(option, state.n);
    }

    wantedOptions(overrides = {}, requireComplete = false) {
      const wanted = new Map();

      for (const option of this.product.options || []) {
        const position = Number(option.position);
        const garment = this.garmentForOption(option);

        if (garment) {
          const state = overrides[garment] || this.state[garment];

          if (!state?.n) {
            if (requireComplete) return null;
            continue;
          }

          const value = this.valueForState(garment, state);
          if (!value) return null;
          wanted.set(position, value.name);
          continue;
        }

        const fitGarment = this.fitGarmentForOption(option);
        if (fitGarment) {
          const fit = (overrides[fitGarment] || this.state[fitGarment])?.fit;
          if (fit) wanted.set(position, fit);
          continue;
        }

        const current =
          (option.values || []).find((value) => value.selected) ||
          (option.values || []).find((value) => value.available !== false) ||
          (option.values || [])[0];

        if (current) wanted.set(position, current.name);
      }

      return wanted;
    }

    hasCustomSelection(overrides = {}) {
      return this.garments.some((garment) => {
        const state = overrides[garment] || this.state[garment];
        return this.isCustom(state?.n);
      });
    }

    customFallbackVariant(overrides = {}) {
      if (!this.variants.length || !this.customValue) return null;
      const wanted = new Map();

      for (const option of this.product.options || []) {
        const position = Number(option.position);
        const garment = this.garmentForOption(option);

        if (garment) {
          const custom = this.optionValue(option, this.customValue);
          if (!custom) return null;
          wanted.set(position, custom.name);
          continue;
        }

        const fitGarment = this.fitGarmentForOption(option);
        if (fitGarment) {
          const fit = (overrides[fitGarment] || this.state[fitGarment])?.fit;
          if (fit) {
            wanted.set(position, fit);
            continue;
          }
        }

        const current =
          (option.values || []).find((value) => value.selected) ||
          (option.values || []).find((value) => value.available !== false) ||
          (option.values || [])[0];
        if (current) wanted.set(position, current.name);
      }

      return (
        this.variants.find(
          (variant) =>
            variant.a !== false &&
            [...wanted.entries()].every(
              ([position, name]) => norm(variant.o[position - 1]) === norm(name)
            )
        ) || null
      );
    }


    /*
      Made-to-order carrier fallback.

      The house matrix can contain standard sizes that are intentionally not
      represented as literal Shopify variants (for example 64R on older product
      data). The exact customer instruction is stored as a line-item property.
      When no exact size variant exists, use an available carrier variant only
      when all matching non-size variants share one price. This keeps checkout
      safe while allowing the complete canonical house matrix to remain usable.
    */
    carrierVariant(overrides = {}) {
      if (!this.variants.length) return null;

      const wanted = new Map();

      for (const option of this.product.options || []) {
        const position = Number(option.position);
        const garment = this.garmentForOption(option);
        if (garment) continue;

        const fitGarment = this.fitGarmentForOption(option);
        if (fitGarment) {
          const fit = (overrides[fitGarment] || this.state[fitGarment])?.fit;
          if (fit) {
            wanted.set(position, fit);
            continue;
          }
        }

        const current =
          (option.values || []).find((value) => value.selected) ||
          (option.values || []).find((value) => value.available !== false) ||
          (option.values || [])[0];
        if (current) wanted.set(position, current.name);
      }

      const candidates = this.variants.filter(
        (variant) =>
          variant.a !== false &&
          [...wanted.entries()].every(
            ([position, name]) => norm(variant.o[position - 1]) === norm(name)
          )
      );

      if (!candidates.length) return null;

      /* Never use a carrier when the matching variants have different prices. */
      const prices = new Set(candidates.map((variant) => String(variant.p ?? '')));
      if (prices.size !== 1 || prices.has('')) return null;

      const currentId = String(
        this.formEl?.querySelector('[name="id"]')?.value ||
          new URL(window.location.href).searchParams.get('variant') ||
          ''
      );

      return (
        candidates.find((variant) => String(variant.i) === currentId) ||
        candidates[0] ||
        null
      );
    }

    variantExistsFor(overrides = {}) {
      if (!this.variants.length) return true;
      const wanted = this.wantedOptions(overrides, false);
      if (!wanted) return false;

      const exact = this.variants.some(
        (variant) =>
          variant.a !== false &&
          [...wanted.entries()].every(
            ([position, name]) => norm(variant.o[position - 1]) === norm(name)
          )
      );

      if (exact) return true;

      /* Standard house sizes remain selectable when checkout can safely use
         either the real Custom carrier or a same-price non-size carrier variant. */
      return Boolean(
        this.customFallbackVariant(overrides) || this.carrierVariant(overrides)
      );
    }

    isOffered(garment, number, lengthCode) {
      if (this.isCustom(number) || lengthCode === 'C') {
        const option = this.optionFor(garment);
        if (!option) return false;
        /* Shopify can mark Custom unavailable while the other garment is still
           on a standard option value. Let the customer choose the direct
           Custom option here; the finished multi-option variant is validated
           before purchase. */
        return Boolean(this.optionValue(option, this.customValue));
      }

      if (
        !this.isCustom(number) &&
        this.hasPreset(garment) &&
        !this.presets[garment][lengthCode].has(String(number))
      ) {
        return false;
      }

      const candidate = {
        ...this.state[garment],
        n: this.isCustom(number) ? this.customValue : String(number),
        l: this.isCustom(number) || lengthCode === 'C' ? '' : lengthCode
      };

      const option = this.optionFor(garment);
      if (option) {
        const value = this.valueForState(garment, candidate);

        /* Prefer a literal standard-size option when it exists. If Shopify does
           not expose that exact option/combination, the made-to-order Custom
           fallback keeps the canonical house size selectable and the chosen
           size is still written to line-item properties. */
        if (!value || value.available === false) {
          return Boolean(
            this.customFallbackVariant({ [garment]: candidate }) ||
              this.carrierVariant({ [garment]: candidate })
          );
        }
      }

      return this.variantExistsFor({ [garment]: candidate });
    }

    lengthHasStock(garment, lengthCode) {
      if (lengthCode === 'C') return this.customOffered(garment);

      return this.numbers[garment].some(
        (number) => !this.isCustom(number) && this.isOffered(garment, number, lengthCode)
      );
    }

    /* Custom is selectable when the garment option contains that direct
       Shopify option value. Final combination availability is checked later. */
    customOffered(garment) {
      if (!this.customValue) return false;

      /*
        Custom is a service path, not a stock length. If the dedicated customizer
        is present, always let the customer enter it. Variant compatibility is
        validated after the customization is saved.
      */
      if (this.findCustomizer()) return true;

      const option = this.optionFor(garment);
      if (!option || !this.optionValue(option, this.customValue)) return false;
      if (!this.variants.length) return true;
      return Boolean(this.customFallbackVariant() || this.carrierVariant());
    }

    findCustomizer() {
      return (
        this.productRoot?.querySelector?.('sh-customizer') ||
        document.querySelector('sh-customizer')
      );
    }

    launchCustomizer(garment = this.view) {
      const customizer = this.findCustomizer();
      if (!customizer) {
        this.showMsg('Custom sizing is available through our tailoring service. Please contact us for assistance.');
        return false;
      }

      this.close();

      window.setTimeout(() => {
        if (typeof customizer.openFromSizing === 'function') {
          customizer.openFromSizing({ garment, source: 'size-panel' });
        } else if (typeof customizer.open === 'function') {
          customizer.open();
        } else {
          customizer.querySelector('[data-shc-open]')?.click();
        }
      }, 260);

      return true;
    }

    /* ------------------------------------------------------ inline size guide */

    async loadSizeGuideSource() {
      if (!this.sizeGuideEnabled || !this.sizeGuideUrl) return null;
      if (this.sizeGuideSource) return this.sizeGuideSource;
      if (this.sizeGuidePromise) return this.sizeGuidePromise;

      this.sizeGuidePromise = (async () => {
        try {
          const response = await fetch(this.sizeGuideUrl, {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });

          if (!response.ok) throw new Error(`Size guide request failed (${response.status})`);

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const source = doc.querySelector(
            'sh-size-guide-tables, [data-section-type="suit-heritage-size-guide-tables"], .shsg-section'
          );

          if (!source) throw new Error('Size guide tables were not found');

          this.sizeGuideSource = source;
          return source;
        } catch (error) {
          console.warn('[sh-size] size guide:', error);
          return null;
        } finally {
          this.sizeGuidePromise = null;
        }
      })();

      return this.sizeGuidePromise;
    }

    chartMatches(card, garment) {
      const requested = garment === 'pants' ? 'pants' : 'jacket';
      const explicit = String(card?.dataset?.shsgGarment || '').toLowerCase();

      if (explicit === 'jacket' || explicit === 'pants') {
        return explicit === requested;
      }

      const title = String(
        card?.querySelector?.('.shsg-table-card__title')?.textContent || ''
      ).toLowerCase();

      if (requested === 'jacket' && title.includes('jacket')) return true;
      if (
        requested === 'pants' &&
        (title.includes('pant') || title.includes('trouser'))
      ) return true;

      /*
        Last-resort structural classifier. This makes the embedded drawer robust
        even if a merchant later renames the table titles in the Theme Editor.
      */
      const headers = Array.from(card?.querySelectorAll?.('thead th') || [])
        .map((node) => String(node.textContent || '').trim().toLowerCase())
        .join(' | ');

      if (requested === 'jacket') {
        return headers.includes('your chest') || headers.includes('jacket chest');
      }

      return (
        headers.includes('your waist') ||
        headers.includes('pant waist') ||
        headers.includes('pant hip') ||
        headers.includes('total rise')
      );
    }

    buildSizeGuideClone(source, garment) {
      const clone = source.cloneNode(true);

      clone
        .querySelectorAll(
          '.shsg-notice, .shsg-section-header, .shsg-tabs-wrap, .shsg-panel__heading, .shsg-note'
        )
        .forEach((node) => node.remove());

      /* Keep the embedded chart on the same Men/Women profile as this product template. */
      clone.querySelectorAll('[data-shsg-panel], .shsg-panel').forEach((panel) => {
        const panelProfile = String(panel.dataset?.shsgPanel || '').toLowerCase();
        if ((panelProfile === 'men' || panelProfile === 'women') && panelProfile !== this.sizingProfile) {
          panel.remove();
        }
      });

      clone.querySelectorAll('[data-shsg-table-card], .shsg-table-card').forEach((card) => {
        if (!this.chartMatches(card, garment)) {
          card.remove();
          return;
        }

        card.hidden = false;
        card.removeAttribute('hidden');
        card.style.removeProperty('display');
      });

      clone.querySelectorAll('[data-shsg-panel], .shsg-panel').forEach((panel) => {
        const hasCard = panel.querySelector('[data-shsg-table-card], .shsg-table-card');
        if (!hasCard) panel.remove();
        else {
          panel.hidden = false;
          panel.removeAttribute('hidden');
        }
      });

      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clone.removeAttribute?.('id');

      return clone;
    }

    async renderSizeGuideFor(garment = this.view) {
      if (!this.chartContent) return false;

      const requestedGarment = garment === 'pants' ? 'pants' : 'jacket';
      const renderToken = ++this.sizeGuideRenderToken;
      this.sizeGuideGarment = requestedGarment;

      if (this.chartTitle) {
        this.chartTitle.textContent =
          requestedGarment === 'jacket' ? 'Jacket Size Chart' : 'Pant Size Chart';
      }

      /*
        Clear the previous garment immediately. The same native dialog is reused
        for Jacket and Pant, so keeping the old DOM visible while a fetch/render
        is pending is what allowed a Jacket chart to appear inside Pant sizing.
      */
      this.chartContent.innerHTML =
        '<div class="shz-chart-panel__loading" role="status">Loading size chart…</div>';

      const source = await this.loadSizeGuideSource();

      /* A later Jacket/Pant request owns the shared drawer now. */
      if (
        renderToken !== this.sizeGuideRenderToken ||
        requestedGarment !== this.view ||
        requestedGarment !== this.sizeGuideGarment
      ) {
        return false;
      }

      if (!source) {
        this.chartContent.innerHTML =
          `<p class="shz-chart-panel__error">We could not load the size chart. ` +
          `<a href="${this.sizeGuideUrl}">Open the full Size Guide</a>.</p>`;
        return false;
      }

      const clone = this.buildSizeGuideClone(source, requestedGarment);
      const matchingCards = clone.querySelectorAll(
        '[data-shsg-table-card], .shsg-table-card'
      );

      if (!matchingCards.length) {
        this.chartContent.innerHTML =
          `<p class="shz-chart-panel__error">The ${requestedGarment === 'jacket' ? 'jacket' : 'pant'} size chart could not be found. ` +
          `<a href="${this.sizeGuideUrl}">Open the full Size Guide</a>.</p>`;
        return false;
      }

      this.chartContent.replaceChildren(clone);

      /*
        Match the size-chart S/R/L view to the length currently selected above.
        Custom has no chart length, so Regular is the neutral fallback.
      */
      const preferred = ['S', 'R', 'L'].includes(this.draft.l)
        ? this.draft.l.toLowerCase()
        : 'r';
      const option = this.chartContent.querySelector(
        `[data-shsg-length-option="${preferred}"]`
      );
      option?.click();

      return true;
    }

    async showSizeGuide() {
      if (!this.sizeGuideEnabled || !this.chartPanel) return;

      const garment = this.view === 'pants' ? 'pants' : 'jacket';

      this.chartPanel.hidden = false;
      this.dialog.classList.add('has-size-chart');
      this.chartToggle?.setAttribute('aria-expanded', 'true');
      if (this.chartToggle) this.chartToggle.textContent = 'Hide Size Charts';

      await this.renderSizeGuideFor(garment);

      /* Do not scroll a stale request after the drawer switched garment. */
      if (garment !== this.view || this.chartPanel.hidden) return;

      requestAnimationFrame(() => {
        this.chartPanel.scrollIntoView({ block: 'nearest' });
      });
    }

    hideSizeGuide(focus = false) {
      if (!this.chartPanel) return;

      ++this.sizeGuideRenderToken;
      this.sizeGuideGarment = '';
      this.chartPanel.hidden = true;
      this.dialog?.classList.remove('has-size-chart');
      this.chartToggle?.setAttribute('aria-expanded', 'false');
      if (this.chartToggle) this.chartToggle.textContent = this.sizeGuideLabel;

      if (focus) this.chartToggle?.focus({ preventScroll: true });
    }

    toggleSizeGuide() {
      if (!this.chartPanel) return;
      if (this.chartPanel.hidden) this.showSizeGuide();
      else this.hideSizeGuide(true);
    }

    /* --------------------------------------------------------- open/close */

    open(garment) {
      garment = garment === 'pants' ? 'pants' : 'jacket';
      if (!this.garments.includes(garment)) garment = this.garments[0];

      this.view = garment;
      this.draft = {
        n: this.state[garment].n,
        l: this.state[garment].l || this.defaultLength(garment)
      };

      this.titleEl.textContent =
        garment === 'jacket'
          ? this.copy.jacketTitle || 'Select a jacket size'
          : this.copy.pantsTitle || 'Select a pant size';

      this.gridTitle.textContent =
        garment === 'jacket' ? 'Jacket size' : 'Pant size';

      this.renderLengths();
      this.renderFit(garment);
      this.renderGrid(garment);
      this.paintCells();
      this.clearMsg();

      if (this.chartPanel && !this.chartPanel.hidden) {
        this.renderSizeGuideFor(garment).catch((error) => {
          console.warn('[sh-size] inline size chart render:', error);
        });
      }

      if (!this.dialog.open) {
        this.dialog.showModal();
        document.body.classList.add('shz-lock');
        requestAnimationFrame(() => this.dialog.classList.add('is-in'));
      }

      this.querySelector('.shz-body').scrollTop = 0;
    }

    close() {
      if (!this.dialog.open) return;

      this.hideSizeGuide(false);

      this.dialog.classList.remove('is-in');
      document.body.classList.remove('shz-lock');

      const finish = () => {
        if (this.dialog.open) this.dialog.close();
      };

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) finish();
      else setTimeout(finish, 240);
    }

    defaultLength(garment) {
      if (this.lengthHasStock(garment, 'R')) return 'R';
      const first = LENGTHS.find((length) => this.lengthHasStock(garment, length.code));
      return first ? first.code : 'R';
    }

    /* ----------------------------------------------------------- renderers */

    renderLengths() {
      const garment = this.view;
      this.segRow.replaceChildren();
      this.segRow.style.setProperty('--shz-seg-cols', String(LENGTHS.length));

      LENGTHS.forEach((length) => {
        const enabled = this.lengthHasStock(garment, length.code);
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'shz-seg__btn';
        button.dataset.length = length.code;
        /* Built as nodes, not innerHTML: the help text is merchant-entered and
           contains quotes and apostrophes. */
        const strong = document.createElement('b');
        strong.textContent = this.lengthLabels[length.code] || length.label;
        button.appendChild(strong);

        if (this.help[length.code]) {
          const small = document.createElement('small');
          small.textContent = this.help[length.code];
          button.appendChild(small);
        }

        button.classList.toggle('is-on', this.draft.l === length.code);
        button.classList.toggle('is-off', !enabled);
        button.setAttribute('aria-pressed', this.draft.l === length.code ? 'true' : 'false');
        button.setAttribute('aria-disabled', enabled ? 'false' : 'true');

        button.addEventListener('click', () => {
          if (!enabled) return;
          this.draft.l = length.code;

          if (length.isCustom) {
            /*
              Custom is a real selectable fourth tab. Mark it selected, hide the
              numeric grid, then hand off to the actual Suit Heritage customizer.
            */
            this.draft.n = this.customValue;
          } else if (this.isCustom(this.draft.n)) {
            this.draft.n = '';
          } else if (this.draft.n && !this.isOffered(garment, this.draft.n, length.code)) {
            /* Keep the number if it exists in the new length, otherwise clear it. */
            this.draft.n = '';
          }

          this.segRow.querySelectorAll('.shz-seg__btn').forEach((node) => {
            const on = node.dataset.length === length.code;
            node.classList.toggle('is-on', on);
            node.setAttribute('aria-pressed', on ? 'true' : 'false');
          });

          this.paintCells();
          this.clearMsg();

          if (length.isCustom) {
            requestAnimationFrame(() => this.launchCustomizer(garment));
          }
        });

        this.segRow.appendChild(button);
      });
    }

    renderFit(garment) {
      if (!this.fitWrap) return;

      const name =
        garment === 'jacket' ? this.optionNames.jacketFit : this.optionNames.pantsFit;
      const option = this.findOption(name);
      const values = option?.values || [];

      if (values.length < 2) {
        this.fitWrap.hidden = true;
        this.state[garment].fit = values[0]?.name || this.state[garment].fit || '';
        return;
      }

      this.fitWrap.hidden = false;
      this.fitLabel.textContent =
        garment === 'jacket'
          ? this.copy.jacketFit || 'Jacket fit'
          : this.copy.pantsFit || 'Pant fit';

      if (!this.state[garment].fit) {
        const selected = values.find((value) => value.selected) || values[0];
        this.state[garment].fit = selected?.name || '';
      }

      this.fitRow.replaceChildren();
      this.fitRow.style.setProperty('--shz-seg-cols', String(values.length));

      values.forEach((value) => {
        const button = document.createElement('button');
        const on = norm(this.state[garment].fit) === norm(value.name);

        button.type = 'button';
        button.className = 'shz-seg__btn';
        const strong = document.createElement('b');
        strong.textContent = value.name;
        button.appendChild(strong);
        button.classList.toggle('is-on', on);
        button.classList.toggle('is-off', value.available === false);
        button.setAttribute('aria-pressed', on ? 'true' : 'false');

        button.addEventListener('click', () => {
          if (value.available === false) return;
          this.state[garment].fit = String(value.name);
          this.renderFit(garment);
          this.paintCells();
        });

        this.fitRow.appendChild(button);
      });
    }

    renderGrid(garment) {
      if (this.grids[garment]) {
        if (this.gridWrap.firstChild !== this.grids[garment][0]) {
          this.gridWrap.replaceChildren(...this.grids[garment]);
        }
        return;
      }

      const numbers = this.numbers[garment];

      if (!numbers.length) {
        const empty = document.createElement('p');
        empty.className = 'shz-empty';
        empty.textContent = 'No sizes have been set up for this product yet.';
        this.grids[garment] = [empty];
        this.gridWrap.replaceChildren(empty);
        return;
      }

      const cells = numbers.map((number) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = this.isCustom(number) ? 'shz-cell shz-cell--custom' : 'shz-cell';
        button.dataset.number = number;

        button.addEventListener('click', () => {
          if (button.classList.contains('is-off')) return;
          this.draft.n = number;
          this.paintCells();
          this.clearMsg();
        });

        return button;
      });

      this.grids[garment] = cells;
      this.gridWrap.replaceChildren(...cells);
    }

    paintCells() {
      const garment = this.view;
      const length = this.draft.l;
      const cells = this.grids[garment] || [];
      const isCustomLength = length === 'C';

      /* Custom is a complete made-to-measure choice, not a numbered length.
         Hide the entire size field so stale XL labels can never remain visible. */
      if (this.gridField) this.gridField.hidden = isCustomLength;
      if (this.gridWrap) this.gridWrap.hidden = isCustomLength;

      if (isCustomLength) {
        this.paintConfirm();
        return;
      }

      cells.forEach((cell) => {
        const number = cell.dataset.number;
        if (!number) return;

        /* Each length tab shows only the sizes that belong to that house matrix.
           Do not fill Long with crossed-out Short/Regular numbers. */
        const inPreset =
          !this.hasPreset(garment) ||
          Boolean(this.presets[garment]?.[length]?.has(String(number)));

        cell.hidden = !inPreset;
        if (!inPreset) {
          cell.classList.remove('is-off', 'is-on');
          cell.tabIndex = -1;
          return;
        }

        const offered = this.isOffered(garment, number, length);
        const chosen = offered && this.draft.n === number;

        cell.textContent = this.isCustom(number) ? this.customValue : this.formatStandardSize(number, length);
        cell.classList.toggle('is-off', !offered);
        cell.classList.toggle('is-on', chosen);
        cell.setAttribute('aria-disabled', offered ? 'false' : 'true');
        cell.setAttribute('aria-pressed', chosen ? 'true' : 'false');
        cell.tabIndex = offered ? 0 : -1;
        cell.setAttribute(
          'aria-label',
          `${garment === 'jacket' ? 'Jacket' : 'Pant'} size ${
            this.isCustom(number) ? this.customValue : this.formatStandardSize(number, length)
          }` +
            (offered ? '' : ' — unavailable')
        );
      });

      this.paintConfirm();
    }

    paintConfirm() {
      const length = this.draft.l;
      const custom = this.isCustom(this.draft.n) || length === 'C';

      this.confirmBtn.disabled = !this.draft.n;
      this.confirmBtn.textContent = this.draft.n
        ? `${this.confirmLabel} · ${
            custom ? this.customValue : `${this.draft.n}${length}`
          }`
        : this.confirmLabel;
    }

    applyExternalSelections(selections = {}) {
      const previous = JSON.parse(JSON.stringify(this.state));
      let changed = false;

      const parse = (raw) => {
        const match = String(raw || '')
          .trim()
          .toUpperCase()
          .match(/^(\d{1,3})\s*(S|R|L)$/);
        return match ? { n: match[1], l: match[2] } : null;
      };

      for (const garment of this.garments) {
        const raw = garment === 'jacket' ? selections.jacket : selections.pants;
        if (!raw) continue;

        const parsed = parse(raw);
        if (!parsed || !this.isOffered(garment, parsed.n, parsed.l)) {
          this.state = previous;
          return false;
        }

        this.state[garment] = {
          ...this.state[garment],
          n: parsed.n,
          l: parsed.l
        };
        changed = true;
      }

      if (!changed) return true;

      this.remember();
      this.paintTriggers();
      this.writeProperties();

      if (this.complete() && this.variants.length && !this.resolveVariant()) {
        this.state = previous;
        this.remember();
        this.paintTriggers();
        this.writeProperties();
        return false;
      }

      if (this.complete() && !this.applyVariant() && this.variants.length) {
        this.state = previous;
        this.remember();
        this.paintTriggers();
        this.writeProperties();
        return false;
      }

      return true;
    }

    /*
      Called by the suit customizer once its form is saved. Sets every garment
      to the custom size, writes the line item properties, and switches the
      variant — the same work commit() does for a normal size.
    */
    applyCustomSize(targetGarments = null) {
      if (!this.customValue) return false;

      const requested = Array.isArray(targetGarments)
        ? targetGarments
        : targetGarments
          ? [targetGarments]
          : this.garments;

      const targets = requested
        .map((garment) => (garment === 'pant' ? 'pants' : garment))
        .filter((garment, index, list) =>
          this.garments.includes(garment) && list.indexOf(garment) === index
        );

      if (!targets.length) return false;

      const previous = JSON.parse(JSON.stringify(this.state));

      for (const garment of targets) {
        const candidate = {
          ...this.state[garment],
          n: this.customValue,
          l: ''
        };
        const option = this.optionFor(garment);
        const value = option ? this.valueForState(garment, candidate) : true;

        if (!value) return false;
        this.state[garment] = candidate;
      }

      /*
        A partial MTM choice (for example Pants only) deliberately leaves the
        other garment's existing standard size intact. Variant resolution is
        attempted only when the complete selection can actually be resolved.
      */
      if (this.complete() && this.variants.length && !this.resolveVariant()) {
        this.state = previous;
        return false;
      }

      this.remember();
      this.paintTriggers();
      this.writeProperties();

      if (this.complete() && !this.applyVariant() && this.variants.length) {
        this.state = previous;
        this.remember();
        this.paintTriggers();
        this.writeProperties();
        return false;
      }

      this.close();
      return true;
    }

    /*
      Called by the customizer when a customer switches a previously saved
      made-to-measure configuration back to standard sizing. Only Custom
      selections are cleared; existing standard garment selections are kept.
      The normal submit guard then requires any cleared size to be chosen again.
    */
    clearCustomSize() {
      let changed = false;

      this.garments.forEach((garment) => {
        if (!this.isCustom(this.state[garment].n)) return;
        this.state[garment].n = '';
        this.state[garment].l = '';
        changed = true;
      });

      if (!changed) return true;
      this.remember();
      this.paintTriggers();
      this.writeProperties();
      return true;
    }

    /* -------------------------------------------------------------- commit */

    commit() {
      const garment = this.view;
      if (!this.draft.n) return;

      if (!this.isOffered(garment, this.draft.n, this.draft.l)) {
        this.showMsg('That size combination is not available. Pick another one.', true);
        return;
      }

      const previous = { ...this.state[garment] };

      this.state[garment].n = this.draft.n;
      this.state[garment].l =
        this.isCustom(this.draft.n) || this.draft.l === 'C' ? '' : this.draft.l;

      const unresolved = this.complete() && this.variants.length && !this.resolveVariant();

      if (unresolved) {
        this.state[garment] = previous;
        this.showMsg('That size combination is sold out. Pick another one.', true);
        return;
      }

      this.remember();
      this.paintTriggers();
      this.writeProperties();

      /* On a suit, jump straight to the next unset panel instead of closing. */
      const next = this.garments.find((item) => !this.state[item].n);
      if (next && next !== garment) {
        this.open(next);
        return;
      }

      if (!this.applyVariant() && this.variants.length) {
        this.state[garment] = previous;
        this.remember();
        this.paintTriggers();
        this.writeProperties();
        this.showMsg('That size combination is sold out. Pick another one.', true);
        return;
      }

      this.close();
    }

    paintTriggers() {
      this.garments.forEach((garment) => {
        const value = this.formatted(garment);
        const output = this.querySelector(`[data-shz-value="${garment}"]`);
        const trigger = this.querySelector(`[data-shz-open="${garment}"]`);

        if (output) output.textContent = value;
        trigger?.classList.toggle('is-set', Boolean(value));
      });

      if (this.note && this.complete()) {
        this.note.hidden = true;
        this.note.classList.remove('is-error');
      }
    }

    formatStandardSize(number, length) {
      if (!number || !length) return '';
      if (this.sizingProfile === 'women') {
        if (length === 'S') return `${number}P`;
        if (length === 'R') return `${number}`;
        if (length === 'L') return `${number}T`;
      }
      return `${number}${length}`;
    }

    formatted(garment) {
      const value = this.state[garment];
      if (this.isCustom(value.n)) return this.customValue;
      return this.formatStandardSize(value.n, value.l);
    }

    complete() {
      return this.garments.every((garment) => Boolean(this.formatted(garment)));
    }

    remember() {
      MEMORY.set(this.memoryKey, JSON.parse(JSON.stringify(this.state)));
    }

    restore() {
      const saved = MEMORY.get(this.memoryKey);

      if (saved) {
        this.state = saved;
      } else {
        this.parse('jacket', this.readProperty('Jacket Size'));
        this.parse('pants', this.readProperty('Pant Size'));
        this.state.jacket.fit = this.readProperty('Jacket Fit');
        this.state.pants.fit = this.readProperty('Pant Fit');

        const params = new URLSearchParams(window.location.search);
        this.parse('jacket', params.get('jacket') || '');
        this.parse('pants', params.get('pants') || '');
      }
    }

    parse(garment, value) {
      if (this.isCustom(value)) {
        this.state[garment].n = this.customValue;
        this.state[garment].l = '';
        return;
      }

      const raw = String(value || '').trim().toUpperCase();
      const match = this.sizingProfile === 'women'
        ? raw.match(/^(00|0|2|4|6|8|10|12|14|16|18)(P|T)?$/)
        : raw.match(/^(\d{1,3})(S|R|L)$/);

      if (!match) return;
      this.state[garment].n = match[1];
      this.state[garment].l = this.sizingProfile === 'women'
        ? (match[2] === 'P' ? 'S' : match[2] === 'T' ? 'L' : 'R')
        : match[2];
    }

    /* --------------------------------------------------- form + properties */

    /*
      A product page carries more than one /cart/add form: the real one and the
      Shop Pay installments form rendered by the price block, which usually
      comes first in the DOM. Only the real one has an "add" button.
    */
    findForm() {
      /* A card passes its own form id explicitly — several cart forms sit on a
         collection page and the panel must bind to its own. */
      const named = this.dataset.shzForm
        ? document.getElementById(this.dataset.shzForm)
        : null;
      if (named) return named;

      const scope = this.productRoot || document;
      const forms = [
        ...scope.querySelectorAll('form[action*="/cart/add"]'),
        ...document.querySelectorAll('form[action*="/cart/add"]')
      ];

      return (
        forms.find((form) => isRealCartForm(form)) ||
        forms.find((form) => !/installment/i.test(form.id)) ||
        forms[0] ||
        null
      );
    }

    /* target defaults to the main form, but a sticky bar submits its own. */
    writeProperties(target) {
      this.formEl = this.formEl?.isConnected ? this.formEl : this.findForm();
      const form = target || this.formEl;
      if (!form) return;

      this.clearProperties(form);

      if (this.panelType === 'suit' || this.panelType === 'jacket') {
        this.setProperty(form, 'Jacket Size', this.formatted('jacket'));
        this.setProperty(form, 'Jacket Fit', this.state.jacket.fit);
      }

      if (this.panelType === 'suit' || this.panelType === 'pants') {
        this.setProperty(form, 'Pant Size', this.formatted('pants'));
        this.setProperty(form, 'Pant Fit', this.state.pants.fit);
      }

      /* A Custom size is made to measure — say so on the order. */
      if (this.complete()) {
        const madeToMeasure = this.garments.some((garment) =>
          this.isCustom(this.state[garment].n)
        );
        this.setProperty(
          form,
          'Sizing Method',
          madeToMeasure ? 'Made to measure' : 'Standard size'
        );
      }
    }

    setProperty(form, name, value) {
      if (!value) return;

      const field = `properties[${name}]`;
      let input = form.querySelector(`input[name="${CSS.escape(field)}"]`);

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = field;
        input.dataset.shzProp = 'true';
        form.appendChild(input);
      }

      input.value = value;
    }

    clearProperties(form) {
      form.querySelectorAll('input[name^="properties["]').forEach((input) => {
        const match = input.name.match(/^properties\[(.*)\]$/);
        if (match && OWNED_PROPS.includes(match[1]) && input.dataset.shzProp) {
          input.remove();
        }
      });
    }

    readProperty(name) {
      if (!this.formEl) return '';
      const input = this.formEl.querySelector(
        `input[name="${CSS.escape(`properties[${name}]`)}"]`
      );
      return input?.value || '';
    }

    guardForm() {
      installGuard();
      if (this.formEl) this.formEl.shzOwner = this;
    }

    /* ------------------------------------------------------------ variants */

    applyVariant() {
      const variant = this.resolveVariant();
      if (!variant) return false;

      this.formEl = this.formEl?.isConnected ? this.formEl : this.findForm();
      const idInput = this.formEl?.querySelector('[name="id"]');

      if (idInput && String(idInput.value) !== String(variant.i)) {
        idInput.value = String(variant.i);
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /* Nudge the theme's own variant inputs so price and SKU stay in sync. */
      this.garments.forEach((garment) => {
        const option = this.optionFor(garment);
        if (!option) return;

        const resolvedName = variant.o?.[Number(option.position) - 1];
        if (resolvedName) this.setNativeOption(option, resolvedName);
      });

      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.i));
      window.history.replaceState({}, '', url.toString());
      return true;
    }

    resolveVariant() {
      if (!this.variants.length) return null;

      const wanted = this.wantedOptions({}, true);

      if (wanted) {
        const exact =
          this.variants.find(
            (variant) =>
              variant.a !== false &&
              [...wanted.entries()].every(
                ([position, name]) => norm(variant.o[position - 1]) === norm(name)
              )
          ) || null;

        if (exact) return exact;
      }

      /* The exact standard size remains the order instruction in properties.
         Prefer the store's Custom carrier, otherwise a same-price carrier that
         preserves every non-size option. */
      return this.customFallbackVariant() || this.carrierVariant();
    }

    setNativeOption(option, value) {
      const scope = this.productRoot || document;
      const position = Number(option.position);
      const selector =
        `input[type="radio"][name="option-${position}"], ` +
        `input[type="radio"][name="options[${CSS.escape(option.name)}]"], ` +
        `select[name="option-${position}"], ` +
        `select[name="options[${CSS.escape(option.name)}]"]`;

      const fields = [...scope.querySelectorAll(selector)];

      const radio = fields.find(
        (field) => field.type === 'radio' && norm(field.value) === norm(value)
      );

      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const select = fields.find((field) => field.tagName === 'SELECT');
      if (select && norm(select.value) !== norm(value)) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* -------------------------------------------------------------- lookups */

    optionFor(garment) {
      const name =
        garment === 'jacket' ? this.optionNames.jacket : this.optionNames.pants;
      /* Stores name this option differently — Blazer Size, Coat Size, Trouser
         Size. Match any of the usual synonyms so the panel still binds to the
         real Shopify option when the configured name does not match exactly. */
      const words =
        garment === 'jacket'
          ? ['jacket', 'blazer', 'coat', 'suit']
          : ['pant', 'trouser', 'bottom', 'waist'];

      return (
        this.findOption(name) ||
        (this.product.options || []).find((option) => {
          const label = norm(option.name);
          return words.some((w) => label.includes(w)) && label.includes('size');
        }) ||
        (this.garments.length === 1
          ? (this.product.options || []).find((option) =>
              norm(option.name) === 'size'
            )
          : null) ||
        null
      );
    }

    garmentForOption(option) {
      return this.garments.find((garment) => this.optionFor(garment) === option) || null;
    }

    fitGarmentForOption(option) {
      if (norm(option.name) === norm(this.optionNames.jacketFit)) return 'jacket';
      if (norm(option.name) === norm(this.optionNames.pantsFit)) return 'pants';
      return null;
    }

    findOption(name) {
      const target = norm(name);
      if (!target) return null;
      return (
        (this.product.options || []).find((option) => norm(option.name) === target) ||
        null
      );
    }

    optionValue(option, name) {
      const target = norm(name);
      if (!target || !option) return null;
      return (
        (option.values || []).find((value) => norm(value.name) === target) || null
      );
    }

    /* -------------------------------------------------------------- helpers */

    showMsg(text, isError) {
      this.msg.textContent = text;
      this.msg.classList.toggle('is-error', Boolean(isError));
    }

    clearMsg() {
      this.msg.textContent = '';
      this.msg.classList.remove('is-error');
    }

    showNote(text) {
      if (!this.note) return;
      this.note.hidden = false;
      this.note.textContent = text;
      this.note.classList.add('is-error');
    }

    readJSON(selector, fallback) {
      const node = this.querySelector(selector);
      if (!node) return fallback;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.warn('[sh-size] bad JSON in', selector, error);
        return fallback;
      }
    }
  }

  customElements.define('sh-size', SHSize);

  /*
    The suit customizer fires this once its form is saved. Every size panel on
    the page switches itself to the custom size in response.
  */
  document.addEventListener('sh-size:apply-custom', (event) => {
    const scope = (event.detail && event.detail.root) || document;
    scope.querySelectorAll('sh-size').forEach((panel) => {
      if (typeof panel.applyCustomSize === 'function') {
        panel.applyCustomSize(event.detail?.garments || null);
      }
    });
  });

  document.addEventListener('sh-size:apply-standard', (event) => {
    const detail = event.detail || {};
    const scope = detail.root || document;
    scope.querySelectorAll('sh-size').forEach((panel) => {
      if (typeof panel.applyExternalSelections === 'function') {
        panel.applyExternalSelections(detail);
      }
    });
  });

  /* Expose the icon helper for the Liquid template's inline markup. */
  window.SHSizeIcons = { svg };
})();